import { Component } from '@theme/component';
import { StitchSwapEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread } from '@theme/utilities';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import {
  SELECTED_COLOR_CHANGE_EVENT,
  findColorInputToSelect,
  normalizeColorValue,
  readSelectedColor,
  stitchSupportsColor,
  stitchVariantIdForColor,
} from '@theme/selected-color-storage';

const ADD_TO_CART_LOCK = 'data-stitch-swap-lock';

/**
 * Holds the add to cart buttons while another product is loading, so a tap
 * during the swap cannot add the product that is being replaced.
 */
function lockAddToCart() {
  for (const button of document.querySelectorAll('main [ref="addToCartButton"]')) {
    if (!(button instanceof HTMLButtonElement) || button.disabled || button.hasAttribute(ADD_TO_CART_LOCK)) continue;
    if (button.getAttribute('aria-disabled') === 'true') continue;
    button.setAttribute(ADD_TO_CART_LOCK, button.dataset.loading === 'true' ? 'loading' : '');
    button.setAttribute('aria-disabled', 'true');
    button.dataset.loading = 'true';
  }
}

function unlockAddToCart() {
  for (const button of document.querySelectorAll(`[${ADD_TO_CART_LOCK}]`)) {
    if (!(button instanceof HTMLElement)) continue;
    if (button.getAttribute(ADD_TO_CART_LOCK) !== 'loading') delete button.dataset.loading;
    button.removeAttribute(ADD_TO_CART_LOCK);
    button.removeAttribute('aria-disabled');
  }
}

window.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element) || !event.target.closest(`[${ADD_TO_CART_LOCK}]`)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);

window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const guarded = [...document.querySelectorAll(`[${ADD_TO_CART_LOCK}]`)].some(
      (button) => form.contains(button) || (form.id && button.getAttribute('form') === form.id)
    );
    if (!guarded) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);

const STITCH_SWAP_MORPH_OPTIONS = {
  ...MORPH_OPTIONS,
  getNodeKey(node) {
    if (
      node instanceof Element &&
      (node.tagName === 'VARIANT-PICKER' || node.tagName === 'PRODUCT-FORM-COMPONENT')
    ) {
      const productId = node.getAttribute('data-product-id');
      if (productId) return `${node.tagName}:${productId}`;
    }
    if (node instanceof Element && node.tagName === 'STITCH-SIZE-PICKER') {
      return 'STITCH-SIZE-PICKER';
    }
    return node instanceof Element ? node.id : undefined;
  },
};

export default class StitchSizePicker extends Component {

  #abortController;
  #swapToken = 0;
  #pendingProductUrl;
  #focusRestoreSelector = null;
  #onSelectedColorChange = () => this.syncStitchAvailability();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#handleChange.bind(this));
    this.addEventListener('click', this.#handleClick.bind(this), true);
    document.addEventListener(SELECTED_COLOR_CHANGE_EVENT, this.#onSelectedColorChange);
    this.syncStitchAvailability();
    this.#syncEntryProductRows();
  }

  /**
   * Shows only the product row belonging to the checked stitch entry, so the
   * sizes above the entry buttons always belong to the selected entry.
   */
  #syncEntryProductRows() {
    const checkedEntry = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[data-stitch-entry]:checked')
    );
    if (!checkedEntry) return;

    const activeIndex = checkedEntry.dataset.stitchEntry;

    for (const row of this.querySelectorAll('[data-stitch-entry-products]')) {
      const isActive = row.getAttribute('data-stitch-entry-products') === activeIndex;
      row.setAttribute('data-entry-active', isActive ? 'true' : 'false');
      row.toggleAttribute('hidden', !isActive);
    }

    const activeRow = this.querySelector('[data-stitch-entry-products][data-entry-active="true"]');
    const selectedProduct = activeRow?.querySelector('input:checked');
    const selectionLabel = this.querySelector('[data-key="stitch-entry-value"]');
    if (selectionLabel) {
      selectionLabel.textContent = [selectedProduct?.value, checkedEntry.value].filter(Boolean).join(' ');
    }
  }

  /**
   * Disables the stitches whose product does not offer the color stored by the
   * variant picker, so only the stitches reachable in that color stay clickable.
   *
   * Only the size stitch row carries `data-color-values`; the stitch entry row
   * and its product rows stay selectable whatever color is chosen.
   */
  syncStitchAvailability() {
    const normalizedColor =
      normalizeColorValue(readSelectedColor()) || normalizeColorValue(this.dataset.selectedColor);

    const inputs = /** @type {HTMLInputElement[]} */ ([
      ...this.querySelectorAll('input[type="radio"][data-color-values]'),
    ]);

    for (const input of inputs) {
      const supported = stitchSupportsColor(input, normalizedColor);

      const keepEnabled = supported || input.checked;

      input.dataset.colorAvailable = supported ? 'true' : 'false';

      if (keepEnabled) {
        input.removeAttribute('disabled');
        if (input.dataset.soldOut !== 'true') input.removeAttribute('aria-disabled');
      } else {
        input.setAttribute('disabled', '');
        input.setAttribute('aria-disabled', 'true');
      }
    }
  }

  /**
   * `disabled` inputs do not fire events, but the surrounding label still
   * forwards clicks — stop them so a disabled stitch cannot trigger a swap.
   *
   * @param {Event} event
   */
  #handleClick(event) {
    const input = event.target instanceof Element ? event.target.closest('label')?.querySelector('input') : null;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.dataset.colorAvailable === 'false' && !input.checked) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (event.target === input && input.dataset.connectedProductUrl) {
      this.#handleChange(event);
    }
  }

  #handleChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.checked) return;
    if (target.dataset.colorAvailable === 'false') return;

    this.#updateCheckedState(target);
    this.#focusRestoreSelector = this.contains(document.activeElement) ? this.#checkedSelectorFor(target) : null;

    if (target.dataset.stitchEntry != null) {
      const matchedProduct = this.#selectMatchingEntryProduct(target);
      this.#syncEntryProductRows();

      const matchedUrl = matchedProduct?.dataset.connectedProductUrl;
      if (matchedUrl && !this.#sameProductUrl(matchedUrl, window.location.pathname)) {
        this.#swapToProduct(this.#productUrlForSelectedColor(matchedProduct));
      }
      return;
    }

    if (!target.dataset.connectedProductUrl) return;

    this.#swapToProduct(this.#productUrlForSelectedColor(target));
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {string}
   */
  #productUrlForSelectedColor(input) {
    const productUrl = input.dataset.connectedProductUrl ?? '';
    const variantId = stitchVariantIdForColor(input, normalizeColorValue(readSelectedColor()));
    if (!variantId) return productUrl;

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);
    return `${url.pathname}${url.search}`;
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {string} Selector for the checked input of the group the input belongs to.
   */
  #checkedSelectorFor(input) {
    if (input.dataset.stitchEntry != null) return 'input[data-stitch-entry]:checked';
    if (input.closest('[data-stitch-entry-products]')) return '[data-stitch-entry-products][data-entry-active="true"] input:checked';
    return 'fieldset:not([data-stitch-entries]) input:checked';
  }

  #restoreFocus() {
    const selector = this.#focusRestoreSelector;
    this.#focusRestoreSelector = null;
    if (!selector) return;
    const picker = this.isConnected ? this : document.querySelector('main stitch-size-picker');
    const input = picker?.querySelector(selector);
    if (input instanceof HTMLElement) input.focus({ preventScroll: true });
  }

  /** @param {string} text */
  #announce(text) {
    if (!text) return;
    let region = document.getElementById('StitchSizePickerStatus');
    if (!region) {
      region = document.createElement('div');
      region.id = 'StitchSizePickerStatus';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      document.body.append(region);
    }
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = text;
    });
  }

  /**
   * Carries the size selected in the outgoing entry row over to the entry just
   * picked: the product offering the same size is checked, so switching
   * `With lid` to `No lid` keeps size 10 and lands on the `No lid` 10 product.
   * Falls back to the row's own checked product when that size is not offered.
   *
   * @param {HTMLInputElement} entryInput - The stitch entry radio just checked.
   * @returns {HTMLInputElement | null} The product radio now checked, when it changed the selection.
   */
  #selectMatchingEntryProduct(entryInput) {
    const nextRow = this.querySelector(
      `[data-stitch-entry-products="${CSS.escape(entryInput.dataset.stitchEntry ?? '')}"]`
    );
    if (!nextRow) return null;

    const previousRow = this.querySelector('[data-stitch-entry-products][data-entry-active="true"]');
    const previousSize = /** @type {HTMLInputElement | null} */ (
      previousRow?.querySelector('input:checked')
    )?.value;

    const candidates = /** @type {HTMLInputElement[]} */ ([...nextRow.querySelectorAll('input[type="radio"]')]);
    const matched =
      candidates.find((radio) => previousSize != null && radio.value === previousSize) ??
      candidates.find((radio) => radio.checked) ??
      candidates[0];

    if (!matched) return null;

    for (const radio of candidates) {
      const isTheOne = radio === matched;
      radio.checked = isTheOne;
      radio.dataset.previousChecked = radio.dataset.currentChecked === 'true' ? 'true' : 'false';
      radio.dataset.currentChecked = isTheOne ? 'true' : 'false';
    }

    return matched;
  }

  #updateCheckedState(target) {
    const group = target.closest('[data-stitch-entry-products], [data-stitch-entries-group], fieldset');
    if (!group) return;

    for (const radio of group.querySelectorAll('input')) {
      if (radio === target) continue;
      radio.dataset.previousChecked = radio.dataset.currentChecked === 'true' ? 'true' : 'false';
      radio.dataset.currentChecked = 'false';
    }

    target.dataset.currentChecked = 'true';
    target.dataset.previousChecked = 'false';
  }

  #swapToProduct(productUrl) {
    if (this.#pendingProductUrl === productUrl) return;
    this.#pendingProductUrl = productUrl;
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const token = ++this.#swapToken;

    this.setAttribute('data-loading', 'true');
    lockAddToCart();

    this.#fetchProductHtml(productUrl)
      .then((html) => {
        if (token !== this.#swapToken) return;

        return this.#resolveColorSelection(html, productUrl).then(
          ({ html: finalHtml, variantId }) => {
            if (token !== this.#swapToken) return;

            this.#updateMain(finalHtml, productUrl);
            this.#updateSelectedThemeVariablesFromHtml(finalHtml);
            this.#updateHead(finalHtml);
            this.#trackRecentlyViewed(finalHtml);

            document.dispatchEvent(new StitchSwapEvent(productUrl));

            const url = new URL(productUrl, window.location.origin);
            if (variantId) {
              url.searchParams.set('variant', variantId);
            }

            if (url.href !== window.location.href) {
              return yieldToMainThread().then(() => {
                if (token !== this.#swapToken) return;
                history.pushState({}, '', url.toString());
              });
            }
          }
        );
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        throw error;
      })
      .finally(() => {
        if (token === this.#swapToken) {
          this.#pendingProductUrl = undefined;
          this.removeAttribute('data-loading');
          unlockAddToCart();
        }
      });
  }

  /**
   * @param {string} url
   * @returns {Promise<Document>}
   */
  #fetchProductHtml(url) {
    return fetch(url, { signal: this.#abortController?.signal })
      .then((response) => response.text())
      .then((responseText) => new DOMParser().parseFromString(responseText, 'text/html'));
  }

  /**
   * Picks the color stored before the swap — or the first color when the new
   * product does not carry it — and re-renders the product with that color
   * selected when it differs from the server's default selection.
   *
   * @param {Document} html
   * @param {string} productUrl
   * @returns {Promise<{ html: Document, variantId: string | undefined }>}
   */
  #resolveColorSelection(html, productUrl) {
    const picker = html.querySelector('variant-picker[data-template-product-match="true"]');
    const input = picker ? findColorInputToSelect(picker) : null;

    if (!input) return Promise.resolve({ html, variantId: undefined });

    const variantId = input.dataset.variantId || input.dataset.firstAvailableOrFirstVariantId;

    if (input.hasAttribute('checked') || !variantId) {
      return Promise.resolve({ html, variantId });
    }

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);

    return this.#fetchProductHtml(url.toString()).then((reRenderedHtml) => ({
      html: reRenderedHtml,
      variantId,
    }));
  }

  #updateMain(newHtml, activeProductUrl) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    for (const radio of main.querySelectorAll('[data-current-checked], [data-previous-checked]')) {
      radio.removeAttribute('data-current-checked');
      radio.removeAttribute('data-previous-checked');
    }

    this.#destroyRecommendationSwipers(main);

    morph(main, newMain, STITCH_SWAP_MORPH_OPTIONS);

    this.#syncStitchFieldsets(activeProductUrl);
    this.syncStitchAvailability();
    this.#syncEntryProductRows();
    this.#reinitRecommendationSwipers(main);
    this.#restoreFocus();
    this.#announce(main.querySelector('h1')?.textContent?.trim() ?? '');
  }

  #reinitRecommendationSwipers(main) {
    for (const recommendations of main.querySelectorAll('product-recommendations')) {
      recommendations.reinitSwipers?.();
    }
  }

  #destroyRecommendationSwipers(main) {
    for (const slider of main.querySelectorAll('[data-product-recommendations-swiper]')) {
      slider.swiper?.destroy(true, true);
      delete slider.dataset.swiperInitializing;
    }
  }

  #syncStitchFieldsets(activeProductUrl) {
    for (const entries of this.querySelectorAll('[data-stitch-entries]')) {
      const entryRadios = [...entries.querySelectorAll('input[data-stitch-entry]')];
      const activeProduct = activeProductUrl
        ? [...entries.querySelectorAll('[data-stitch-entry-products] input')].find((radio) =>
            this.#sameProductUrl(radio.dataset.connectedProductUrl, activeProductUrl)
          )
        : null;
      const activeIndex = activeProduct?.closest('[data-stitch-entry-products]')?.dataset.stitchEntryProducts;
      const selectedEntry = entryRadios.find((radio) => radio.dataset.stitchEntry === activeIndex)
        || entryRadios.find((radio) => radio.hasAttribute('checked'));

      for (const radio of entryRadios) {
        radio.checked = radio === selectedEntry;
        radio.dataset.currentChecked = radio.checked ? 'true' : 'false';
        radio.dataset.previousChecked = 'false';
      }
    }
    this.#syncEntryProductRows();

    const groups = [...this.querySelectorAll('fieldset:not([data-stitch-entries])'), ...this.querySelectorAll('[data-stitch-entry-products]')];

    for (const group of groups) {
      const radios = [...group.querySelectorAll('input')];
      const shouldBeChecked = activeProductUrl
        ? radios.find((radio) => this.#sameProductUrl(radio.dataset.connectedProductUrl, activeProductUrl))
        : radios.find((radio) => radio.hasAttribute('checked'));
      if (!shouldBeChecked && !group.hasAttribute('data-stitch-entry-products')) continue;

      for (const radio of radios) {
        const isTheOne = radio === shouldBeChecked;
        radio.checked = isTheOne;
        radio.dataset.currentChecked = isTheOne ? 'true' : 'false';
      }
    }
  }

  #sameProductUrl(a, b) {
    if (!a || !b) return false;
    try {
      return new URL(a, window.location.origin).pathname === new URL(b, window.location.origin).pathname;
    } catch {
      return false;
    }
  }

  #trackRecentlyViewed(newHtml) {
    const newProductId = newHtml.querySelector('variant-picker[data-product-id]')?.dataset.productId;
    if (newProductId) RecentlyViewed.addProduct(newProductId);
  }

  #updateHead(newHtml) {
    const newTitle = newHtml.querySelector('title');
    if (newTitle) document.title = newTitle.textContent ?? document.title;

    const newCanonical = newHtml.querySelector('link[rel="canonical"]');
    const currentCanonical = document.head.querySelector('link[rel="canonical"]');
    if (newCanonical && currentCanonical) {
      currentCanonical.setAttribute('href', newCanonical.getAttribute('href') ?? '');
    }

    for (const newMeta of newHtml.head.querySelectorAll('meta[name], meta[property]')) {
      const attr = newMeta.hasAttribute('name') ? 'name' : 'property';
      const key = newMeta.getAttribute(attr);
      if (!key) continue;
      const currentMeta = document.head.querySelector(`meta[${attr}="${CSS.escape(key)}"]`);
      if (currentMeta) currentMeta.setAttribute('content', newMeta.getAttribute('content') ?? '');
    }
  }

  #updateSelectedThemeVariablesFromHtml(newHtml) {
    const root = document.documentElement.style;
    const variables = [
      '--selected-variant-color',
      '--selected-variant-text-color',
      '--selected-variant-primary-background',
      '--selected-variant-secondary-background',
    ];
    const styles = [...newHtml.querySelectorAll('style')];

    for (const variable of variables) {
      let nextValue = '';

      for (const style of styles) {
        const cssText = style.textContent ?? '';
        if (!cssText.includes(':root') || !cssText.includes(variable)) continue;

        const match = cssText.match(new RegExp(`${variable}\\s*:\\s*([^;]+);`));
        if (match?.[1]) nextValue = match[1].trim();
      }

      if (nextValue) {
        root.setProperty(variable, nextValue);
      } else {
        root.removeProperty(variable);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    document.removeEventListener(SELECTED_COLOR_CHANGE_EVENT, this.#onSelectedColorChange);
  }
}

if (!customElements.get('stitch-size-picker')) {
  customElements.define('stitch-size-picker', StitchSizePicker);
}

window.addEventListener('popstate', () => {
  const picker = document.querySelector('variant-picker[data-template-product-match="true"]');
  const renderedProductUrl = picker?.dataset.productUrl;
  if (!renderedProductUrl) return;

  const renderedPath = new URL(renderedProductUrl, window.location.origin).pathname;
  if (renderedPath !== window.location.pathname) window.location.reload();
});
